import {
  buildWorkPackageOwnerFilter,
  PlanningWorkPackageSlider,
  SuspenseLoader,
  TOAST_ID_WORK_PACKAGE,
  useDateRange,
  usePlanning,
} from '@bechtel/client-common';
import {
  belowBreakpoint,
  Box,
  Icon,
  IconSize,
  Text,
  Theme,
  useMediaQuery,
  useSideSheetState,
  useToastsDispatch,
} from '@bechtel/gds';
import { CrewMemberAssignment, Scalars, WorkPackage } from '@bechtel/types';
import { t } from '@lingui/macro';
import { FC, Suspense, useContext, useEffect, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { EditWorkPackageSchedule } from '~/components/organisms/edit-work-package-schedule';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import PlanningDateWindow, { workPackagesOutsideWLA } from './planning-date-window';
import { TextStyled } from './styled';

export interface WorkPackageHeaderProps {
  workPackageTag: WorkPackage['tag'];
  startDate?: WorkPackage['startDate'];
  endDate?: WorkPackage['endDate'];
  selectedWorkpackageList?: WorkPackage[];
  showRemainingTime?: Scalars['Boolean'];
  workOwners?: CrewMemberAssignment[];
  isPlanningModule?: Scalars['Boolean'];
}

export const WorkPackageHeader: FC<WorkPackageHeaderProps> = ({
  workPackageTag,
  startDate,
  endDate,
  selectedWorkpackageList,
  showRemainingTime = false,
  isPlanningModule = false,
  workOwners,
}) => {
  const {
    weekStartDate,
    weekEndDate,
    selectedSuperintendent,
    selectedGeneralForeman,
    selectedForeman,
    planningWorkPackageSlider,
    totalSelectedCount,
    workPackageFilterInput,
  } = usePlanning();
  const [dateRangeLabel, dateDistance] = useDateRange(startDate, endDate);
  const { visible, open, close } = useSideSheetState(false);
  const { canEditSchedule } = useWorkPackagePermissions(workOwners);
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const dispatchToast = useToastsDispatch();

  // Use totalSelectedCount when "All" is selected, otherwise use actual list length
  const displayCount = useMemo(() => {
    if (isPlanningModule && planningWorkPackageSlider === PlanningWorkPackageSlider.All && totalSelectedCount) {
      return totalSelectedCount;
    }
    return selectedWorkpackageList?.length || 0;
  }, [isPlanningModule, planningWorkPackageSlider, totalSelectedCount, selectedWorkpackageList]);

  // Build filter owners similar to planning workpackage list
  const filterOwners = useMemo(() => {
    if (!isPlanningModule) return undefined;

    return buildWorkPackageOwnerFilter({
      selectedSuperintendent,
      selectedGeneralForeman,
      selectedForeman,
      filterByUnassignedPersona: [], // No drawer values in work package header
    });
  }, [isPlanningModule, selectedSuperintendent, selectedGeneralForeman, selectedForeman]);

  // Determine if "Apply to All" should be true
  const applyToAll = useMemo(() => {
    if (!isPlanningModule) return false;

    // applyToAll should be true ONLY when the "All" button is selected in the work package selection mode
    // This is independent of crew hierarchy selections
    return planningWorkPackageSlider === PlanningWorkPackageSlider.All;
  }, [isPlanningModule, planningWorkPackageSlider]);

  const workPackageListOutsideWLA = useMemo(() => {
    if (isPlanningModule && selectedWorkpackageList && selectedWorkpackageList.length)
      return workPackagesOutsideWLA(selectedWorkpackageList, weekStartDate, weekEndDate);
    return [];
  }, [isPlanningModule, selectedWorkpackageList, weekStartDate, weekEndDate]);

  useEffect(() => {
    if (workPackageListOutsideWLA.length == 0)
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: TOAST_ID_WORK_PACKAGE } });
  }, [dispatchToast, workPackageListOutsideWLA]);

  // Conditional logic for enabling edit schedule
  const canEnableEditSchedule = isPlanningModule
    ? canEditSchedule &&
      selectedWorkpackageList &&
      selectedWorkpackageList.length > 0 &&
      (workPackageFilterInput?.filterByOwners?.length ?? 0) > 0
    : canEditSchedule && selectedWorkpackageList && selectedWorkpackageList.length > 0;

  return (
    <Box
      pl={{ sm: '600' }}
      pt={{ sm: '500' }}
      pb={{ sm: '400' }}
      flexDirection={{ sm: 'column' }}
      justifyContent={{ sm: 'center' }}
      css={canEnableEditSchedule ? 'cursor: pointer;' : 'cursor: auto;'}
      onClick={() => canEnableEditSchedule && open()}
    >
      <Box py={{ sm: '200' }}>
        <Text
          size={isPlanningModule && isDesktop && selectedWorkpackageList && displayCount > 1 ? '500' : '600'}
          color="white"
          weight="600"
          transform="uppercase"
        >
          {selectedWorkpackageList && displayCount > 1
            ? `${t`Multiple Work Packages`} ${isDesktop ? '' : t`Selected`}(${displayCount})`
            : workPackageTag
              ? workPackageTag
              : t`No Work Package Selected`}
        </Text>
      </Box>
      {!showRemainingTime ? (
        <Box mt={{ sm: '200' }} pt={{ sm: '200' }} pb={{ sm: '300' }}>
          {selectedWorkpackageList && selectedWorkpackageList.length > 0 && (
            <>
              <Icon size={IconSize.MEDIUM} icon="calendar-day" fillColor="white"></Icon>
              <TextStyled size={isPlanningModule && isDesktop ? '300' : '400'}>{dateRangeLabel}</TextStyled>
              {isPlanningModule && workPackageListOutsideWLA.length > 0 && (
                <PlanningDateWindow
                  workPackageOutsideWLACount={workPackageListOutsideWLA.length}
                  selectedWorkPackageCount={selectedWorkpackageList.length}
                />
              )}
            </>
          )}
        </Box>
      ) : (
        <Box mb={{ sm: '400' }} pt={{ sm: '100' }} pb={{ sm: '100' }} alignItems={{ sm: 'center' }}>
          {selectedWorkpackageList && selectedWorkpackageList.length > 0 && (
            <>
              <Box pr={{ sm: '400' }}>
                <Icon size={IconSize.MEDIUM} icon="calendar-day" fillColor="white"></Icon>
              </Box>
              <Box pt={{ sm: '200' }}>
                <Text size="400" color="white">
                  {dateRangeLabel} {dateDistance}
                </Text>
              </Box>
              {isPlanningModule && workPackageListOutsideWLA.length > 0 && (
                <PlanningDateWindow
                  workPackageOutsideWLACount={workPackageListOutsideWLA.length}
                  selectedWorkPackageCount={selectedWorkpackageList.length}
                />
              )}
            </>
          )}
        </Box>
      )}
      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
        {canEnableEditSchedule && visible && (
          <EditWorkPackageSchedule
            workPackageList={selectedWorkpackageList}
            startDate={startDate}
            endDate={endDate}
            onClose={close}
            workPackageTag={workPackageTag}
            isPlanningModule={isPlanningModule}
            filterByOwners={filterOwners}
            applyToAll={applyToAll}
          ></EditWorkPackageSchedule>
        )}
      </Suspense>
    </Box>
  );
};
